import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Box,
  Typography,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import useAppStore from '../store/useAppStore';
import { scheduleAPI } from '../services/apiWithAuth';

const HolidayManager = ({ open, onClose }) => {
  const { accounts } = useMsal();
  const {
    holidays,
    employees,
    addHoliday,
    removeHoliday,
    loadHolidays,
    setNotification,
    setScheduleData
  } = useAppStore();

  const getCurrentUserEmail = () => {
    if (accounts && accounts.length > 0) {
      return accounts[0].username || 'user';
    }
    return 'user';
  };

  const [editingHoliday, setEditingHoliday] = useState(null);
  const [newHoliday, setNewHoliday] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
    name: '',
    recurring: true
  });
  const [showAddForm, setShowAddForm] = useState(false);

  // Load holidays when dialog opens
  useEffect(() => {
    if (open) {
      const currentYear = new Date().getFullYear();
      loadHolidays(currentYear);
    }
  }, [open, loadHolidays]);

  // Sort holidays by date
  const sortedHolidays = [...holidays].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    if (a.month !== b.month) return a.month - b.month;
    return a.day - b.day;
  });

  // Generate options for day select based on selected month and year
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.name.trim()) {
      setNotification({
        type: 'warning',
        message: 'Wprowadź nazwę święta'
      });
      return;
    }

    // Check if holiday already exists
    const exists = holidays.some(h => 
      h.month === newHoliday.month && 
      h.day === newHoliday.day
    );

    if (exists) {
      setNotification({
        type: 'warning',
        message: 'Święto na ten dzień już istnieje'
      });
      return;
    }

    const success = await addHoliday(newHoliday.year, newHoliday.month, newHoliday.day, newHoliday.name, newHoliday.recurring);
    
    if (success) {
      // Usuń wszystkie wpisy pracowników dla tego dnia
      try {
        console.log('Clearing schedules for holiday:', newHoliday.year, newHoliday.month, newHoliday.day);
        const deletePromises = employees.map(emp => {
          console.log('Deleting schedule for employee:', emp.code);
          return scheduleAPI.deleteSchedule(emp.code, newHoliday.year, newHoliday.month, newHoliday.day, getCurrentUserEmail())
            .then(() => {
              console.log('Successfully deleted schedule for:', emp.code);
              return true;
            })
            .catch((error) => {
              console.log('Could not delete schedule for:', emp.code, error.message);
              return null;
            });
        });
        await Promise.all(deletePromises);
        
        console.log('Refreshing calendar data...');
        // Odśwież dane kalendarza
        const data = await scheduleAPI.getForMonth(newHoliday.year, newHoliday.month);
        if (data && typeof data === 'object') {
          setScheduleData(data);
        }
        console.log('Calendar data refreshed');
      } catch (error) {
        console.error('Error clearing schedules for holiday:', error);
      }
      
      setNotification({
        type: 'success',
        message: `Dodano święto: ${newHoliday.name}`
      });

      // Reset form
      setNewHoliday({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
        name: '',
        recurring: true
      });
      setShowAddForm(false);
    } else {
      setNotification({
        type: 'error',
        message: 'Błąd podczas dodawania święta'
      });
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    const success = await removeHoliday(holiday.year, holiday.month, holiday.day);
    
    if (success) {
      setNotification({
        type: 'success',
        message: `Usunięto święto: ${holiday.name}`
      });
    } else {
      setNotification({
        type: 'error',
        message: 'Błąd podczas usuwania święta'
      });
    }
  };

  const handleEditHoliday = (holiday) => {
    // Editing disabled for now - holidays are stored in database
    setNotification({
      type: 'info',
      message: 'Edycja świąt zostanie dodana w przyszłej wersji'
    });
  };

  const handleSaveEdit = () => {
    setEditingHoliday(null);
  };

  const handleCancelEdit = () => {
    setEditingHoliday(null);
  };

  const formatDate = (holiday) => {
    if (holiday.recurring) {
      return `${holiday.day.toString().padStart(2, '0')}.${holiday.month.toString().padStart(2, '0')} (co roku)`;
    }
    return `${holiday.day.toString().padStart(2, '0')}.${holiday.month.toString().padStart(2, '0')}.${holiday.year}`;
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
      'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
    ];
    return months[monthNumber - 1];
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🎄 Zarządzanie świętami
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setShowAddForm(!showAddForm)}
            sx={{ mb: 2 }}
          >
            {showAddForm ? 'Anuluj dodawanie' : 'Dodaj nowe święto'}
          </Button>

          {showAddForm && (
            <Box sx={{ 
              p: 2, 
              border: 1, 
              borderColor: 'divider',
              borderRadius: 1, 
              backgroundColor: 'background.paper',
              mb: 2 
            }}>
              <Typography variant="h6" gutterBottom>
                Nowe święto
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Rok"
                  type="number"
                  value={newHoliday.year}
                  onChange={(e) => setNewHoliday(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                  sx={{ width: 120 }}
                  inputProps={{ min: 2020, max: 2050 }}
                />
                
                <FormControl sx={{ width: 150 }}>
                  <InputLabel>Miesiąc</InputLabel>
                  <Select
                    value={newHoliday.month}
                    label="Miesiąc"
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, month: e.target.value }))}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {getMonthName(i + 1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl sx={{ width: 100 }}>
                  <InputLabel>Dzień</InputLabel>
                  <Select
                    value={newHoliday.day}
                    label="Dzień"
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, day: e.target.value }))}
                  >
                    {Array.from({ length: getDaysInMonth(newHoliday.year, newHoliday.month) }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {i + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <TextField
                label="Nazwa święta"
                value={newHoliday.name}
                onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
                placeholder="np. Boże Narodzenie, Nowy Rok, itp."
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newHoliday.recurring}
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, recurring: e.target.checked }))}
                  />
                }
                label="Powtarzaj co roku"
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleAddHoliday}
                  size="small"
                >
                  Dodaj święto
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setShowAddForm(false);
                    setNewHoliday({
                      year: new Date().getFullYear(),
                      month: new Date().getMonth() + 1,
                      day: new Date().getDate(),
                      name: ''
                    });
                  }}
                  size="small"
                >
                  Anuluj
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          Lista świąt ({sortedHolidays.length})
        </Typography>

        {sortedHolidays.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Brak dodanych świąt. Użyj przycisku "Dodaj nowe święto" aby dodać pierwsze święto.
          </Typography>
        ) : (
          <List>
            {sortedHolidays.map((holiday, index) => (
              <ListItem 
                key={`${holiday.year}-${holiday.month}-${holiday.day}`}
                sx={{
                  border: '1px solid #bdbdbd',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: 'background.paper'
                }}
              >
                {editingHoliday && 
                 editingHoliday.year === holiday.year && 
                 editingHoliday.month === holiday.month && 
                 editingHoliday.day === holiday.day ? (
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="Nazwa święta"
                      value={editingHoliday.name}
                      onChange={(e) => setEditingHoliday(prev => ({ ...prev, name: e.target.value }))}
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <IconButton onClick={handleSaveEdit} color="primary" size="small">
                      <SaveIcon />
                    </IconButton>
                    <IconButton onClick={handleCancelEdit} color="default" size="small">
                      <CancelIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="h6" component="span">
                            {holiday.name}
                          </Typography>
                          <Chip 
                            label={formatDate(holiday)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={`${getMonthName(holiday.month)} ${holiday.year}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleEditHoliday(holiday)}
                        sx={{ mr: 1 }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteHoliday(holiday)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidayManager;